import React, { useState } from "react";
import "./Bikes.css";
import BikeStatGauge from "./BikeStatGauge";
import MatchlessLogo from "../../assets/matchlesslogo.png";
import AJSLogo from "../../assets/ajsLogo.png";
import WDLogo from "../../assets/WDMatchlessLogo.png";
import OriginalLogo from "../../assets/OriginalLogo.png";
import OldLogo from "../../assets/OldLogo.png";
import RarityStats from "./RarityStats";
import ManualButton from "../../assets/manual-button.png";
import PartsButton from "../../assets/button_spares.avif";
import BikeManualChatModal from "./BikeManualChatModal";

function addTransparentSuffix(filename) {
  if (!filename) return filename;

  const lastDot = filename.lastIndexOf(".");
  if (lastDot === -1) return filename + "_transparent";

  const base = filename.substring(0, lastDot);
  const ext = filename.substring(lastDot);

  return `${base}_transparent${ext}`;
}

function stripBrand(name) {
  if (!name) return name;

  return name
    .replace(/matchless\s*/i, "")
    .replace(/ajs\s*/i, "")
    .trim();
}

function inferEngineType(engine) {
  const engineLower = (engine || "").toLowerCase();

  if (engineLower.includes("parallel twin")) return "Parallel Twin";
  if (engineLower.includes("v-twin")) return "V-Twin";
  if (engineLower.includes("v-four")) return "V-Four";
  if (engineLower.includes("racing single")) return "Racing Single";
  if (engineLower.includes("single")) return "Single";

  return "";
}

function inferCategory(bike, modelName, description) {
  const desc = (description || "").toLowerCase();
  const model = (modelName || "").toLowerCase();

  if (bike.wd) return "Military";

  if (
    desc.includes("competition") ||
    desc.includes("scrambles") ||
    desc.includes("racing") ||
    model.includes("clubman") ||
    model.includes("rr") ||
    model.includes("g50") ||
    model.includes("typhoon")
  ) {
    return "Competition";
  }

  if (desc.includes("touring")) return "Touring";

  return bike.category || "";
}

function inferRarity(bike) {
  if (bike.rarity) return bike.rarity;
  if (bike.productionCount) return bike.productionCount;
  return "";
}

function BikeCard({ bike, onClick }) {
  const [isFlipped, setIsFlipped] = useState(false);
const [showManualModal, setShowManualModal] = useState(false);
  if (!bike) return null;

  const rawModelName =
    bike.name || bike.modelName || bike.title || "Unknown model";

  const modelName = stripBrand(rawModelName);

  const engine =
    bike.engine ||
    bike.engineSize ||
    bike.capacity ||
    bike.cc ||
    "";

  const engineType = bike.engineType || inferEngineType(engine);

  const years =
    bike.year ||
    bike.yearRange ||
    bike.productionYears ||
    "";

  const description =
    bike.description ||
    bike.summary ||
    bike.text ||
    "";

  const imageFile =
    bike.image ||
    bike.imageUrl ||
    bike.photo;

  const topSpeed = bike.topSpeed || bike.speed || "";
  const horsepower = bike.horsepower || bike.hp || bike.bhp || "";
  const weight = bike.weight || "";
  const gearbox = bike.gearbox || "";

  const boreStroke = bike.boreStroke || "";
  const compression = bike.compression || "";
  const carburettor = bike.carburettor || "";
  const ignition = bike.ignition || "";

  const category = inferCategory(bike, modelName, description);
  const rarity = inferRarity(bike);

  const transformedName = addTransparentSuffix(imageFile);
  let bikeImageSrc = null;

  try {
    bikeImageSrc = require(`../../assets/bikes/${transformedName}`);
  } catch {
    try {
      bikeImageSrc = require(`../../assets/bikes/${imageFile}`);
    } catch {
      bikeImageSrc = null;
    }
  }

  const brandRaw =
    bike.brand ||
    bike.make ||
    (bike.wd ? "WD Matchless" : "Matchless");

  const brand = brandRaw || "";
  const nameLower = (bike.name || "").toLowerCase();

  const yearString = bike.year || bike.years || "";
  const [startYearPart] = yearString.split(/[-–]/);
  const startYear = parseInt(startYearPart, 10);

  let brandLogo = MatchlessLogo;

  if (bike.wd) {
    brandLogo = WDLogo;
  } else if (
    brand.toLowerCase().includes("ajs") ||
    nameLower.includes("ajs")
  ) {
    brandLogo = AJSLogo;
  } else if (
    brand.toLowerCase().includes("matchless") ||
    nameLower.includes("matchless")
  ) {
    if (!isNaN(startYear)) {
      if (startYear < 1920) {
        brandLogo = OriginalLogo;
      } else if (startYear < 1939) {
        brandLogo = OldLogo;
      } else {
        brandLogo = MatchlessLogo;
      }
    } else {
      brandLogo = MatchlessLogo;
    }
  }

  const hasDescription = !!description;

  const handleToggleFlip = () => {
    if (!hasDescription) return;

    setIsFlipped((prev) => !prev);

    if (onClick) {
      onClick(bike);
    }
  };

  const handleBackButtonClick = (e) => {
    e.stopPropagation();
    setIsFlipped(false);
  };

  return (
  <>
    <article
      id={`bike-${(bike.name || modelName)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "")}`}
      className={
        `bike-card ` +
        `${hasDescription ? "bike-card-clickable " : ""}` +
        `${isFlipped ? "bike-card-flipped " : ""}`
      }
      onClick={
        hasDescription && !showManualModal
          ? handleToggleFlip
          : undefined
      }
    >
      <div className="bike-card-watermark">
        <img src={brandLogo} alt={`${brand} logo`} />
      </div>

      <div className="bike-card-inner">
        {/* FRONT */}
        <div className="bike-card-face bike-card-front">
          <div className="bike-card-image-wrapper">
            {bikeImageSrc && (
              <img
                src={bikeImageSrc}
                alt={modelName}
                className="bike-card-image"
              />
            )}
          </div>

          <div className="bike-card-body">
            <div className="bike-card-header">
              <div className="bike-card-title-block">
                {brand && (
                  <p className="bike-card-brand-small">{brand}</p>
                )}

                <h2 className="bike-card-title">{modelName}</h2>

                {engine && (
                  <p className="bike-card-engine">{engine}</p>
                )}

                {(engineType || bike.wd || category || rarity || weight) && (
                  <div className="bike-card-top-tags">
                    {engineType && (
                      <span className="bike-card-type-badge">
                        {engineType}
                      </span>
                    )}

                    {bike.wd && (
                      <span className="bike-card-tag bike-card-tag-military">
                        Military
                      </span>
                    )}

                    {!bike.wd && category && (
                      <span className="bike-card-tag">
                        {category}
                      </span>
                    )}

                    {rarity && (
                      <span className="bike-card-tag bike-card-tag-rarity">
                        {rarity}
                      </span>
                    )}

                    {weight && (
                      <span className="bike-card-tag bike-card-weight-tag">
                        {weight}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className="bike-card-top-right">
                {bike.hasManual && (
                  <button
                    type="button"
                    className="bike-card-manual-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowManualModal(true);
                    }}
                    aria-label={`Consult manual for ${modelName}`}
                  >
                    <img
                      src={ManualButton}
                      alt="Consult manual"
                      className="bike-card-manual-button-image"
                    />
                  </button>
                )}

                {years && (
                  <div className="bike-card-years bike-card-years-below-button">
                    {years}
                  </div>
                )}
              </div>
            </div>

            {(topSpeed || horsepower) && (
              <div className="bike-card-spec-cluster">
                {topSpeed && (
                  <BikeStatGauge
                    value={topSpeed}
                    max={160}
                    label="Top Speed"
                    unit="mph"
                    size={86}
                    color="#5d7259"
                  />
                )}

                {horsepower && (
                  <BikeStatGauge
                    value={horsepower}
                    max={100}
                    label="Power"
                    unit="bhp"
                    size={72}
                    color="#8b3a2e"
                  />
                )}

                <RarityStats
                  estimatedProduction={bike.estimatedProduction}
                  estimatedSurvivors={bike.estimatedSurvivors}
                  estimateConfidence={bike.estimateConfidence}
                />
              </div>
            )}
          </div>
        </div>

        {/* BACK */}
        {hasDescription && (
          <div className="bike-card-face bike-card-back">
            <button
              type="button"
              className="bike-card-back-close"
              onClick={handleBackButtonClick}
              aria-label="Close details"
            >
              ×
            </button>

            <div className="bike-card-back-content">
              <h3 className="bike-card-back-title">
                {bike.name || bike.modelName || modelName}
              </h3>

              {(engine || years) && (
                <p className="bike-card-back-meta">
                  {engine && <span>{engine}</span>}
                  {engine && years && <span> • </span>}
                  {years && <span>{years}</span>}
                </p>
              )}

              <div className="bike-card-back-description">
                {description}
              </div>

              {(boreStroke ||
                compression ||
                carburettor ||
                ignition ||
                horsepower ||
                topSpeed ||
                weight ||
                gearbox) && (
                <div className="bike-card-back-specs">
                  {horsepower && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">Power</span>
                      <span className="bike-card-back-spec-value">
                        {horsepower}
                      </span>
                    </div>
                  )}

                  {topSpeed && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">
                        Top speed
                      </span>
                      <span className="bike-card-back-spec-value">
                        {topSpeed}
                      </span>
                    </div>
                  )}

                  {weight && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">Weight</span>
                      <span className="bike-card-back-spec-value">
                        {weight}
                      </span>
                    </div>
                  )}

                  {gearbox && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">Gearbox</span>
                      <span className="bike-card-back-spec-value">
                        {gearbox}
                      </span>
                    </div>
                  )}

                  {boreStroke && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">
                        Bore × stroke
                      </span>
                      <span className="bike-card-back-spec-value">
                        {boreStroke}
                      </span>
                    </div>
                  )}

                  {compression && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">
                        Compression
                      </span>
                      <span className="bike-card-back-spec-value">
                        {compression}
                      </span>
                    </div>
                  )}

                  {carburettor && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">
                        Carburettor
                      </span>
                      <span className="bike-card-back-spec-value">
                        {carburettor}
                      </span>
                    </div>
                  )}

                  {ignition && (
                    <div className="bike-card-back-spec-row">
                      <span className="bike-card-back-spec-label">
                        Ignition
                      </span>
                      <span className="bike-card-back-spec-value">
                        {ignition}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="bike-card-underline" />
    </article>

    <BikeManualChatModal
      show={showManualModal}
      onHide={() => setShowManualModal(false)}
      bike={bike}
    />
  </>
);
}

export default BikeCard;